// The sphere: the desktop orb's renderer on a web canvas (orb.ts,
// carried over verbatim). The component owns the canvas and the
// mapping; the caller owns what the states mean. Tap on the sphere
// opens and closes the microphone — the orb's own tap, not a button
// beside it.
import { createEffect, createSignal, onCleanup, onMount, type JSX } from "solid-js";
import { createOrb, type OrbState } from "@/orb/orb";

export type OrbHandle = ReturnType<typeof createOrb>;

export function OrbFace(props: {
  state: OrbState;
  // The one line under the sphere: what is being said, answered.
  line: string;
  // A task of the user's running (the orb's own pill).
  task: boolean;
  onTap: () => void;
  // The orb lands here once it exists, for the mic's blocks and the
  // reply's analyser.
  handle: (orb: OrbHandle) => void;
  // The sphere's word for the screen reader: the mic's state.
  label: string;
}): JSX.Element {
  let canvas: HTMLCanvasElement | undefined;
  // A signal, not a plain variable: the mappings below read it
  // reactively, so their first run applies whenever the orb lands,
  // whatever the declaration order.
  const [orb, setOrb] = createSignal<OrbHandle | null>(null);

  onMount(() => {
    const o = createOrb(canvas!, { size: 240 });
    o.setSize(240);
    setOrb(o);
    // The orb's own tap: a press that stayed (moved under 4 px). A
    // canvas onClick would fire after a drag too. The callback reads
    // the props when the orb fires it — its own event, on purpose.
    // eslint-disable-next-line solid/reactivity
    o.onTap(() => props.onTap());
    props.handle(o);
    onCleanup(() => {
      o.destroy();
      setOrb(null);
    });
  });

  createEffect(() => {
    orb()?.setState(props.state);
  });
  createEffect(() => {
    orb()?.setLine(props.line);
  });
  createEffect(() => {
    orb()?.setTask(props.task);
  });

  return (
    <canvas
      ref={canvas}
      class="orb-face"
      width={800}
      height={260}
      role="button"
      tabIndex={0}
      aria-label={props.label}
      onKeyDown={(e) => {
        // Enter works the tap. Space is the page's own hold-to-talk.
        if (e.key === "Enter") {
          e.preventDefault();
          props.onTap();
        }
      }}
    />
  );
}
